import { get, post, put, del } from '../apiClient';
import { API_ENDPOINTS, API_BASE_URL } from '../config';

const certificateService = {
  // Certificate CRUD Operations
  getCertificates: async (queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);
      if (queryParams.employeeId && queryParams.employeeId !== 'all') queryString.append('employeeId', queryParams.employeeId);
      if (queryParams.courseId && queryParams.courseId !== 'all') queryString.append('courseId', queryParams.courseId);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);

      const url = queryString.toString() ? `/certificates?${queryString.toString()}` : '/certificates';
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch certificates'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch certificates'
      };
    }
  },

  getCertificate: async (id) => {
    try {
      const response = await get(`/certificates/${id}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch certificate'
      };
    }
  },

  createCertificate: async (certificateData) => {
    try {
      const response = await post('/certificates', certificateData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to create certificate'
      };
    }
  },

  updateCertificate: async (id, certificateData) => {
    try {
      const response = await put(`/certificates/${id}`, certificateData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to update certificate'
      };
    }
  },

  revokeCertificate: async (id, reason) => {
    try {
      const response = await post(`/certificates/${id}/revoke`, { reason });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to revoke certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to revoke certificate'
      };
    }
  },

  replaceCertificate: async (id, replacementData) => {
    try {
      const response = await post(`/certificates/${id}/replace`, replacementData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to replace certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to replace certificate'
      };
    }
  },

  downloadCertificate: async (id) => {
    try {
      // Use direct response from apiClient (blob)
      const response = await get(`/certificates/${id}/download`, {
        responseType: 'blob'
      });

      if (response) {
        return {
          success: true,
          data: response
        };
      } else {
        return {
          success: false,
          error: 'Failed to download certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to download certificate'
      };
    }
  },

  verifyCertificate: async (code) => {
    try {
      const response = await get(`/certificates/verify/${code}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to verify certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to verify certificate'
      };
    }
  },

  // Certificate Template CRUD Operations
  getTemplates: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();

      if (queryParams.category && queryParams.category !== 'all') queryString.append('category', queryParams.category);
      if (queryParams.isActive !== undefined) queryString.append('isActive', queryParams.isActive);
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);

      const url = queryString.toString() ? `/certificate-templates?${queryString.toString()}` : '/certificate-templates';
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch templates'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch templates'
      };
    }
  },

  getTemplate: async (id) => {
    try {
      const response = await get(`/certificate-templates/${id}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch template'
      };
    }
  },

  // Upload template file to Cloudinary
  uploadTemplateFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'template');

      // Get auth token
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found'
        };
      }

      // Use direct fetch for file uploads
      const response = await fetch(`${API_BASE_URL}/training/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          error: result.responseMessage || 'Failed to upload template file'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to upload template file'
      };
    }
  },

  createTemplate: async (templateData) => {
    try {


      const response = await post('/certificate-templates', templateData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to create template'
      };
    }
  },

  updateTemplate: async (id, templateData) => {
    try {
      const response = await put(`/certificate-templates/${id}`, templateData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to update template'
      };
    }
  },

  deleteTemplate: async (id) => {
    try {
      const response = await del(`/certificate-templates/${id}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete template'
      };
    }
  },

  duplicateTemplate: async (id) => {
    try {
      const response = await post(`/certificate-templates/${id}/duplicate`);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to duplicate template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to duplicate template'
      };
    }
  },

  downloadTemplate: async (id) => {
    try {
      const response = await get(`/certificate-templates/${id}/download`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to download template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to download template'
      };
    }
  },

  // Credit CRUD Operations
  getCredits: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();

      if (queryParams.category && queryParams.category !== 'all') queryString.append('category', queryParams.category);
      if (queryParams.ceuType && queryParams.ceuType !== 'all') queryString.append('ceuType', queryParams.ceuType);
      if (queryParams.courseId && queryParams.courseId !== 'all') queryString.append('courseId', queryParams.courseId);
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);

      const url = queryString.toString() ? `/credits?${queryString.toString()}` : '/credits';
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch credits'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch credits'
      };
    }
  },

  getCredit: async (id) => {
    try {
      const response = await get(`/credits/${id}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch credit'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch credit'
      };
    }
  },

  createCredit: async (creditData) => {
    try {
      const response = await post('/credits', creditData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create credit'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to create credit'
      };
    }
  },

  updateCredit: async (id, creditData) => {
    try {
      const response = await put(`/credits/${id}`, creditData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update credit'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to update credit'
      };
    }
  },

  deleteCredit: async (id) => {
    try {
      const response = await del(`/credits/${id}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete credit'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete credit'
      };
    }
  },

  getCreditsByCourse: async (courseId) => {
    try {
      const response = await get(`/credits/course/${courseId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch course credits'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch course credits'
      };
    }
  },

  getCreditsByCategory: async (category) => {
    try {
      const response = await get(`/credits/category/${category}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch category credits'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch category credits'
      };
    }
  },

  searchCredits: async (query, options = {}) => {
    try {
      const queryString = new URLSearchParams();
      queryString.append('q', query);

      if (options.category) queryString.append('category', options.category);
      if (options.ceuType) queryString.append('ceuType', options.ceuType);

      const url = `/credits/search?${queryString.toString()}`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to search credits'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to search credits'
      };
    }
  },

  // Course Search for Dropdown
  searchCourses: async (query, limit = 20) => {
    try {
      const queryString = new URLSearchParams();
      queryString.append('q', query);
      queryString.append('limit', limit);

      const url = `${API_ENDPOINTS.TRAINING.COURSES}/search?${queryString.toString()}`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to search courses'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to search courses'
      };
    }
  },

  getCoursesForDropdown: async (limit = 50) => {
    try {
      const queryString = new URLSearchParams();
      queryString.append('limit', limit);

      const url = `${API_ENDPOINTS.TRAINING.COURSES}/dropdown?${queryString.toString()}`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch courses'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch courses'
      };
    }
  },

  // Get employees for certificate issue
  getEmployees: async () => {
    try {
      // Use the same endpoint as AssignmentsEnrollment
      const response = await get('/training-assignments/users');

      if (response.responseCode === 200) {
        const usersData = response.data?.users || [];

        // Transform the data to match our expected format
        const transformedUsers = usersData.map(user => ({
          id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          department: user.department,
          role: user.role
        }));

        return {
          success: true,
          data: transformedUsers
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch employees'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch employees'
      };
    }
  },

  // Get certificate templates for certificate issue
  getCertificateTemplates: async () => {
    try {
      const response = await get('/certificate-templates');

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data?.templates || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch templates'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch templates'
      };
    }
  },

  getCertificateTemplate: async (templateId) => {
    try {
      const response = await get(`/certificate-templates/${templateId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch template'
      };
    }
  },

  // Download certificate
  downloadCertificate: async (certificateId) => {
    try {
      const response = await get(`/certificates/${certificateId}/download`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to download certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to download certificate'
      };
    }
  },

  // Revoke certificate
  revokeCertificate: async (certificateId) => {
    try {
      const response = await put(`/certificates/${certificateId}/revoke`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to revoke certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to revoke certificate'
      };
    }
  },

  // Get course credits
  getCourseCredits: async (courseId) => {
    try {
      const response = await get(`/credits/course/${courseId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch course credits'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch course credits'
      };
    }
  },

  // Export certificates data
  exportCertificates: async (queryParams = {}) => {
    try {
      // Get auth token - try both possible token names
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');


      if (!token) {
        return {
          success: false,
          error: 'Authentication token not found. Please login again.'
        };
      }

      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_BASE_URL}/certificates/export?${queryString}` : `${API_BASE_URL}/certificates/export`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // For CSV downloads, return the blob
        if (queryParams.format === 'csv') {
          const blob = await response.blob();
          return {
            success: true,
            data: blob
          };
        } else {
          // For JSON format
          const data = await response.json();
          return {
            success: true,
            data: data.data
          };
        }
      } else {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.message || 'Failed to export certificates'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to export certificates'
      };
    }
  },

  // Get employee certificates (for transcript)
  getEmployeeCertificates: async (employeeId) => {
    try {
      const response = await get(`/certificates/employee/${employeeId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch employee certificates'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch employee certificates'
      };
    }
  },

  // Replace certificate
  replaceCertificate: async (certificateId) => {
    try {
      const response = await put(`/certificates/${certificateId}/replace`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to replace certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to replace certificate'
      };
    }
  },

  // Bulk upload certificates
  bulkUploadCertificates: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Get auth token - try both possible token names
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');


      if (!token) {
        return {
          success: false,
          error: 'No authentication token found'
        };
      }

      const response = await fetch(`${API_BASE_URL}/certificates/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        return {
          success: true,
          data: result.data,
          message: result.responseMessage
        };
      } else {
        return {
          success: false,
          error: result.responseMessage || 'Failed to upload certificates'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to upload certificates'
      };
    }
  },

  // Download bulk template
  downloadBulkTemplate: async () => {
    try {
      // Get auth token - try both possible token names
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found'
        };
      }

      const response = await fetch(`${API_BASE_URL}/certificates/bulk-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulk_certificate_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to download template'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to download template'
      };
    }
  },

  deleteCertificate: async (id, hard = false) => {
    try {
      const response = await del(`/certificates/${id}${hard ? '?hard=true' : ''}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete certificate'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete certificate'
      };
    }
  }
};

export default certificateService;
